import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { sharedLowlight } from './tiptapShared';
import { CodeBlockView } from '../components/CodeBlockView';

const trailingParagraphKey = new PluginKey('codeBlockTrailingParagraph');

function countCodeBlocks(doc: ProseMirrorNode): number {
  let count = 0;
  doc.descendants(node => {
    if (node.type.name === 'codeBlock') count += 1;
  });
  return count;
}

function positionsAfterCodeBlocksAtEndOfParent(doc: ProseMirrorNode): number[] {
  const positions: number[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== 'codeBlock') return true;
    const after = pos + node.nodeSize;
    if (!doc.resolve(after).nodeAfter) positions.push(after);
    return false;
  });
  return positions;
}

export function createCodeBlockExtension() {
  return CodeBlockLowlight.extend({
    addNodeView() {
      return ReactNodeViewRenderer(CodeBlockView);
    },

    addProseMirrorPlugins() {
      return [
        ...(this.parent?.() ?? []),
        new Plugin({
          key: trailingParagraphKey,
          appendTransaction(transactions, oldState, newState) {
            if (!transactions.some(tr => tr.docChanged)) return null;
            if (transactions.every(tr => tr.getMeta('preventUpdate'))) return null;
            if (countCodeBlocks(newState.doc) <= countCodeBlocks(oldState.doc)) return null;

            const inserts = positionsAfterCodeBlocksAtEndOfParent(newState.doc);
            if (!inserts.length) return null;

            const paragraph = newState.schema.nodes.paragraph.create();
            let tr = newState.tr;
            for (let i = inserts.length - 1; i >= 0; i -= 1) {
              tr = tr.insert(inserts[i], paragraph);
            }
            return tr;
          }
        })
      ];
    }
  }).configure({
    lowlight: sharedLowlight,
    defaultLanguage: null
  });
}
